import React, { useState } from 'react';
import {
    Button,
    Heading,
    ButtonContainer,
    Fieldset,
    Layout,
    RadioButton,
    FormFieldRadioButtonGroup,
} from '@vwfs-bronson/bronson-react';
import { Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { dashboardPagePath } from 'components/navigation/paths';
import { getChangePaymentDayEndpoint, PaymentDayChange, FinancialDetails } from 'cpfr-common';
import { currentBrand } from 'config';
import { useTranslationWithFormatting } from 'localization/useTranslationWithFormatting';
import { CpDataApi } from 'cp-xhr';
import { EditStatus } from '../enums';
import { Spinner, useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';
import { PaymentDayNotification } from '../payment-day-notification/PaymentDayNotification';
import { useFocusOnMount } from '../../../../../../utils/useFocusOnMount';
import { paymentDayStatusTagFetchStateActions } from '../non-edit-view/usePaymentDayStatusTagSlice';
import { useDispatch } from 'react-redux';
import { EmailInputModal } from '../../../../../request/form-view/email-input/EmailInputModal';
import { getValidationSchema } from '../validationSchema';
import { getErrorCode, isDuplicateSubmissionError, isPendingRequestError, useSubmitOnce } from 'utils';

type EditViewProps = {
    details?: FinancialDetails;
    contractId: string;
    finishEditing: (editStatus: EditStatus) => void;
};

export const EditView: React.FC<EditViewProps> = ({ /*details,*/ contractId, finishEditing }) => {
    const { isSubmitting, run } = useSubmitOnce();
    const [editStatus, setEditStatus] = useState<EditStatus>(EditStatus.NOT_PERFORMED);
    const { onAction: onEditCancel } = useAnalyticsActionTracker('financialDetailsEditDueDateSectionCancel');
    const { onAction: onEditFail } = useAnalyticsActionTracker('financialDetailsEditDueDateSectionSubmitFailed');
    const { onAction: onSuccess } = useAnalyticsActionTracker('financialDetailsEditDueDateSectionSuccess');
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslationWithFormatting(['financial-details', 'loader']);
    const prefix = `collection-date-section.edit`;

    const radioItems = [1, 5, 15, 20, 25];
    // const currentPaymentDay = details?.collectionDate || 0;
    // radioItems.includes(currentPaymentDay) && radioItems.splice(radioItems.indexOf(currentPaymentDay), 1);
    const [paymentDay, setPaymentDay] = useState(radioItems[0]);
    const defaultCheckedIndex = radioItems.indexOf(paymentDay);

    useFocusOnMount('input[name=newPaymentDay]');

    const createRadioButton = () => {
        return radioItems.map((value, index) => {
            return (
                <RadioButton
                    key={index}
                    defaultChecked={index === defaultCheckedIndex}
                    name="newPaymentDay"
                    value={value}
                    testId={`test-paymentDay-${value}`}
                    onChange={() => {
                        setPaymentDay(value);
                    }}
                >
                    {value}
                </RadioButton>
            );
        });
    };

    const initialValues: PaymentDayChange = {
        newPaymentDay: paymentDay,
    };

    const handleSubmit = (values: PaymentDayChange): Promise<void> =>
        run(async () => {
            setEditStatus(EditStatus.NOT_PERFORMED);

            const preparedData: PaymentDayChange = {
                newPaymentDay: paymentDay,
                customerMail: values.customerMail,
            };

            try {
                await CpDataApi.put(getChangePaymentDayEndpoint(contractId), preparedData);
            } catch (error) {
                // An earlier payment day change is still open at the market, so this
                // one was not applied. Saying "success" here would hide that.
                if (isPendingRequestError(error)) {
                    onEditFail();
                    setEditStatus(EditStatus.ALREADY_IN_PROGRESS);
                    finishEditing(EditStatus.ALREADY_IN_PROGRESS);
                    return;
                }
                // A duplicate is not a failure: the change is already registered,
                // so fall through to the success path instead of offering a retry.
                if (!isDuplicateSubmissionError(error)) {
                    onEditFail();
                    const errorCode = getErrorCode(error);
                    setEditStatus(
                        errorCode === EditStatus.USER_WITH_NO_EMAIL_CODE
                            ? EditStatus.USER_WITH_NO_EMAIL_CODE
                            : EditStatus.ERROR,
                    );
                    return;
                }
            }

            finishEditing(EditStatus.SUCCESS);
            setEditStatus(EditStatus.SUCCESS);
            dispatch(paymentDayStatusTagFetchStateActions.setStatusTag(true));
            onSuccess();
        });

    return (
        <>
            <div data-theme={currentBrand === 'seat' ? 'alternative' : 'primary'} className={'u-mb'}>
                <Heading
                    className="c-data-overview__header u-pt u-mb-none"
                    level={5}
                    id={`collection-date-section-edit-view-${contractId}`}
                >
                    {t(`${prefix}.headline`)}
                    <Button
                        element="a"
                        link
                        className="c-data-overview__button u-mb-small u-ml-xsmall u-mr-small c-icon  c-icon--[semantic-close]"
                        onClick={() => history.push(dashboardPagePath())}
                        testId={'x-back-button'}
                        disabled={!!isSubmitting}
                    >
                        {' '}
                    </Button>
                </Heading>
                {isSubmitting && (
                    <Spinner center>
                        <p>{t('genericText', { ns: 'loader' })}</p>
                    </Spinner>
                )}
                {/* The form stays mounted while submitting so the email typed into
                    EmailInputModal survives a failed attempt; the buttons below are
                    disabled instead. */}
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={getValidationSchema(t, prefix, editStatus === EditStatus.USER_WITH_NO_EMAIL_CODE)}
                >
                    {({ submitForm }) => (
                        <Form onSubmit={(e) => e.preventDefault()}>
                            <Fieldset>
                                <Fieldset.Row>
                                    <Layout>
                                        <Layout.Item default="1/1" s="1/1">
                                            <p>{t(`${prefix}.text`)}</p>
                                            <div className="u-pr">
                                                <FormFieldRadioButtonGroup>
                                                    {createRadioButton()}
                                                </FormFieldRadioButtonGroup>
                                            </div>
                                        </Layout.Item>
                                    </Layout>
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <PaymentDayNotification editStatus={editStatus} />
                                    <p
                                        id="payment-day-warning-alert"
                                        role="alert"
                                        aria-live="polite"
                                        className="u-mb-small u-font-size-fs-1"
                                    >
                                        {t(`${prefix}.warning-notification`)}
                                    </p>
                                    <ButtonContainer className={'u-mt'} center>
                                        <Button
                                            secondary
                                            type="button"
                                            testId="back-to-dashboard"
                                            disabled={isSubmitting}
                                            onClick={(): void => {
                                                onEditCancel();
                                                history.push(dashboardPagePath());
                                            }}
                                        >
                                            {t(`${prefix}.cancel-button`)}
                                        </Button>
                                        <Button
                                            onClick={submitForm}
                                            testId="submit-paymentDay"
                                            type="button"
                                            disabled={isSubmitting}
                                            aria-describedby="payment-day-warning-alert"
                                        >
                                            {t(`${prefix}.confirm-button`)}
                                        </Button>
                                    </ButtonContainer>
                                </Fieldset.Row>
                                <EmailInputModal
                                    name="customerMail"
                                    show={editStatus === EditStatus.USER_WITH_NO_EMAIL_CODE}
                                    onClose={() => setEditStatus(EditStatus.NOT_PERFORMED)}
                                    onConfirm={submitForm}
                                    disabled={isSubmitting}
                                />
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};
